/**
 * TemplateGallery (MKT-REDESIGN-01 M3) — a gallery, not a grid of icons: each
 * template shows a live scaled render of its real HTML (sample values in the
 * tokens), its subject, version and last edit, with Preview / Edit and a menu
 * (Rename, Duplicate, Versions, Archive). "Draft with AI" makes a new template
 * from a one-line brief and drops you in the builder.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Archive, ArchiveRestore, Copy, Eye, History, Loader2, MoreHorizontal, Pencil, Sparkles, Wand2 } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import {
  sampleFill, useArchiveTemplate, useDraftEmail, useDuplicateTemplate, useMarketingTemplates, useRenameTemplate, useRestoreVersion, useTemplateVersions,
  type MarketingTemplate,
} from "./templates";

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
const who = (email: string | null) => (email ?? "").split("@")[0].replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function TemplateGallery({ onPreview }: { onPreview: (t: MarketingTemplate) => void }) {
  const [showArchived, setShowArchived] = useState(false);
  const { data: templates, isLoading } = useMarketingTemplates(showArchived);
  const [menu, setMenu] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<MarketingTemplate | null>(null);
  const [versionsFor, setVersionsFor] = useState<MarketingTemplate | null>(null);
  const [drafting, setDrafting] = useState(false);
  const duplicate = useDuplicateTemplate();
  const archive = useArchiveTemplate();
  const list = templates ?? [];
  const archivedCount = list.filter((t) => t.archived).length;

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 id="mkt-templates" className="inline-flex items-center gap-2 text-base font-bold text-slate-900">Templates</h2>
        <span className="text-xs text-slate-400">{list.filter((t) => !t.archived).length} live{showArchived && archivedCount ? ` · ${archivedCount} archived` : ""}</span>
        <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-500">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-3.5 w-3.5 accent-brand-700" /> Show archived
        </label>
        <button type="button" onClick={() => setDrafting(true)} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-brand-200 bg-brand-50 px-3 text-xs font-semibold text-brand-800 hover:bg-brand-100">
          <Sparkles className="h-3.5 w-3.5" /> Draft with AI
        </button>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-sm text-slate-400">Loading your templates…</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((t) => (
            <div key={t.id} className={cn("group relative flex flex-col overflow-hidden rounded-xl border border-hairline bg-white transition-shadow hover:shadow-sm", t.archived && "opacity-70")}>
              <Thumb html={t.html} onClick={() => onPreview(t)} />
              <div className="flex flex-1 flex-col p-3">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-900" title={t.name}>{t.name}</div>
                    <div className="mt-0.5 line-clamp-2 text-xs text-slate-500">{t.subject || <span className="text-slate-300">no subject line yet</span>}</div>
                  </div>
                  <div className="relative shrink-0">
                    <button type="button" onClick={() => setMenu(menu === t.id ? null : t.id)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="More"><MoreHorizontal className="h-4 w-4" /></button>
                    {menu === t.id && (
                      <div className="absolute right-0 z-20 mt-1 w-44 overflow-hidden rounded-lg border border-hairline bg-white py-1 text-xs shadow-lg" onMouseLeave={() => setMenu(null)}>
                        <MenuItem icon={Pencil} onClick={() => { setMenu(null); setRenaming(t); }}>Rename</MenuItem>
                        <MenuItem icon={Copy} onClick={() => { setMenu(null); duplicate.mutate(t.id); }}>Duplicate</MenuItem>
                        <MenuItem icon={History} onClick={() => { setMenu(null); setVersionsFor(t); }}>Versions (v{t.version})</MenuItem>
                        {t.archived
                          ? <MenuItem icon={ArchiveRestore} onClick={() => { setMenu(null); archive.mutate({ id: t.id, archived: false }); }}>Restore</MenuItem>
                          : <MenuItem icon={Archive} onClick={() => { setMenu(null); archive.mutate({ id: t.id, archived: true }); }} danger>Archive</MenuItem>}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-1 text-[10.5px] text-slate-400">v{t.version} · edited {fmt(t.updated_at)}{t.updated_by ? ` by ${who(t.updated_by)}` : ""}{t.archived ? " · archived" : ""}</div>
                <div className="mt-3 flex items-center gap-2">
                  <button onClick={() => onPreview(t)} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50">
                    <Eye className="h-3.5 w-3.5" /> Preview
                  </button>
                  <Link to={`/email-builder?template=${t.key}&from=marketing`} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Link>
                </div>
              </div>
            </div>
          ))}
          {list.length === 0 && <div className="col-span-full py-10 text-center text-sm text-slate-400">No templates yet — draft one with AI or start in the builder.</div>}
        </div>
      )}

      {renaming && <RenameModal t={renaming} onClose={() => setRenaming(null)} />}
      {versionsFor && <VersionsModal t={versionsFor} onClose={() => setVersionsFor(null)} />}
      {drafting && <DraftModal onClose={() => setDrafting(false)} />}
    </section>
  );
}

/** A real render of the email, scaled to the card — the template's own HTML with sample values. */
function Thumb({ html, onClick }: { html: string | null; onClick: () => void }) {
  const doc = useMemo(() => (html ? sampleFill(html) : ""), [html]);
  return (
    <button type="button" onClick={onClick} className="relative block h-44 w-full overflow-hidden border-b border-hairline bg-[#eef0f3] text-left" title="Preview">
      {doc ? (
        <iframe srcDoc={doc} title="thumbnail" sandbox="allow-same-origin" tabIndex={-1} aria-hidden
          className="pointer-events-none absolute left-1/2 top-0 h-[1100px] w-[640px] origin-top -translate-x-1/2 scale-[0.5] border-0 bg-white" />
      ) : (
        <div className="flex h-full items-center justify-center text-xs text-slate-400">No design yet</div>
      )}
      <span className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-[#eef0f3] to-transparent" />
    </button>
  );
}

function MenuItem({ icon: Icon, children, onClick, danger }: { icon: React.ComponentType<{ className?: string }>; children: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button type="button" onClick={onClick} className={cn("flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50", danger ? "text-rose-700" : "text-slate-700")}>
      <Icon className="h-3.5 w-3.5 text-slate-400" /> {children}
    </button>
  );
}

function RenameModal({ t, onClose }: { t: MarketingTemplate; onClose: () => void }) {
  const [name, setName] = useState(t.name);
  const rename = useRenameTemplate();
  return (
    <Modal open onClose={onClose} title="Rename Template" size="sm">
      <div className="space-y-3">
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) rename.mutate({ id: t.id, name }, { onSuccess: onClose }); }}
          className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-hairline px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
          <button type="button" disabled={!name.trim() || rename.isPending} onClick={() => rename.mutate({ id: t.id, name }, { onSuccess: onClose })} className="rounded-lg bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50">Save</button>
        </div>
      </div>
    </Modal>
  );
}

function VersionsModal({ t, onClose }: { t: MarketingTemplate; onClose: () => void }) {
  const { data: versions, isLoading } = useTemplateVersions(t.id);
  const restore = useRestoreVersion();
  const [peek, setPeek] = useState<string | null>(null);
  return (
    <Modal open onClose={onClose} size="lg"
      title={<span className="inline-flex items-center gap-1.5">Versions — {t.name}<InfoTip label="How restoring works">Restoring an older version saves it as a new version, so nothing is lost.</InfoTip></span>}
      subtitle={`Current is v${t.version}.`}>
      {isLoading ? <p className="py-6 text-center text-sm text-slate-400">Loading…</p> : (versions ?? []).length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">No saved versions yet — the builder snapshots one every time you save.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-[260px_1fr]">
          <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-hairline">
            {(versions ?? []).map((v) => (
              <li key={v.id} className={cn("flex items-center gap-2 px-3 py-2 text-xs", peek === v.id && "bg-brand-50/60")}>
                <button type="button" onClick={() => setPeek(v.id)} className="min-w-0 flex-1 text-left">
                  <div className="font-semibold text-slate-800">v{v.version}{v.version === t.version ? " · current" : ""}</div>
                  <div className="truncate text-slate-500">{fmt(v.saved_at)}{v.saved_by ? ` · ${who(v.saved_by)}` : ""}</div>
                </button>
                {v.version !== t.version && (
                  <button type="button" disabled={restore.isPending} onClick={() => restore.mutate({ templateId: t.id, version: v }, { onSuccess: onClose })}
                    className="shrink-0 rounded-md border border-hairline bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">Restore</button>
                )}
              </li>
            ))}
          </ul>
          <div className="h-80 overflow-hidden rounded-lg border border-hairline bg-[#eef0f3]">
            {peek ? (
              <iframe srcDoc={sampleFill((versions ?? []).find((v) => v.id === peek)?.html ?? "")} title="version" sandbox="allow-same-origin" className="h-full w-full border-0 bg-white" />
            ) : <div className="flex h-full items-center justify-center text-xs text-slate-400">Pick a version to see it</div>}
          </div>
        </div>
      )}
    </Modal>
  );
}

function DraftModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const draft = useDraftEmail();
  const [brief, setBrief] = useState("");
  const [audience, setAudience] = useState("");
  const [tone, setTone] = useState<"warm" | "direct" | "urgent" | "playful">("warm");
  const [name, setName] = useState("");
  return (
    <Modal open onClose={onClose} size="md"
      title={<span className="inline-flex items-center gap-1.5">Draft an Email with AI<InfoTip label="How AI drafts work">AI writes the words; the layout, logo, buttons and unsubscribe footer come from our template. You edit it in the builder before anything is sent.</InfoTip></span>}>
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-slate-500">What's the email about?</span>
          <textarea autoFocus rows={3} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="e.g. Fall furnace tune-up reminder for plan members — book before the cold snap, mention the free filter"
            className="w-full rounded-lg border border-hairline bg-white px-3 py-2 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        </label>
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-slate-500">Who it's for (optional)</span>
            <input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="plan members in Eagan" className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-slate-500">Template name (optional)</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Fall Tune-Up Reminder" className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700" />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-medium text-slate-500">Tone</span>
          {(["warm", "direct", "urgent", "playful"] as const).map((t) => (
            <button key={t} type="button" onClick={() => setTone(t)} className={cn("rounded-full border px-2.5 py-1 text-xs font-medium capitalize", tone === t ? "border-brand-300 bg-brand-50 text-brand-800" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}>{t}</button>
          ))}
        </div>
        {draft.isError && <p className="text-xs font-medium text-rose-600">{(draft.error as Error).message}</p>}
        <div className="flex items-center justify-end gap-2 border-t border-hairline pt-3">
          <button type="button" onClick={onClose} className="rounded-lg border border-hairline px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
          <button type="button" disabled={brief.trim().length < 8 || draft.isPending}
            onClick={() => draft.mutate({ brief, audience, tone, name }, { onSuccess: (t) => { onClose(); navigate(`/email-builder?template=${t.key}&from=marketing`); } })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50">
            {draft.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {draft.isPending ? "Drafting…" : "Draft it — open in the builder"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

